/** A real tablist: arrow keys move, the panel is owned by the selected tab. */

import { useId, useRef, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";

import { cn } from "@/lib/cn";

import "./Tabs.css";

export interface TabItem {
  readonly id: string;
  readonly label: ReactNode;
  readonly count?: number;
  readonly content?: ReactNode;
}

export interface TabsProps {
  /** Names the tablist. */
  readonly label: string;
  readonly value: string;
  readonly onChange: (id: string) => void;
  readonly tabs: readonly TabItem[];
  readonly className?: string;
  readonly style?: CSSProperties;
}

export function Tabs({ label, value, onChange, tabs, className, style }: TabsProps) {
  const base = useId();
  const buttons = useRef<Record<string, HTMLButtonElement | null>>({});
  const selected = tabs.find((tab) => tab.id === value) ?? tabs[0];

  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (step === 0) return;
    const at = tabs.findIndex((tab) => tab.id === (selected?.id ?? ""));
    const next = tabs[(at + step + tabs.length) % tabs.length];
    if (!next) return;
    event.preventDefault();
    onChange(next.id);
    buttons.current[next.id]?.focus();
  };

  return (
    <div className={cn("al-tabs", className)} style={style}>
      <div className="al-tabs-list" role="tablist" aria-label={label} onKeyDown={move}>
        {tabs.map((tab) => {
          const active = selected?.id === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`${base}-tab-${tab.id}`}
              aria-selected={active}
              aria-controls={`${base}-panel-${tab.id}`}
              tabIndex={active ? 0 : -1}
              ref={(node) => {
                buttons.current[tab.id] = node;
              }}
              className={cn("al-tabs-tab", active ? "al-tabs-tab-on" : undefined)}
              onClick={() => onChange(tab.id)}
            >
              {tab.label}
              {tab.count !== undefined ? (
                <span className="al-tabs-count al-tabular">{tab.count}</span>
              ) : null}
            </button>
          );
        })}
      </div>
      {selected?.content !== undefined ? (
        <div
          role="tabpanel"
          id={`${base}-panel-${selected.id}`}
          aria-labelledby={`${base}-tab-${selected.id}`}
          tabIndex={0}
          className="al-tabs-panel"
        >
          {selected.content}
        </div>
      ) : null}
    </div>
  );
}
